import { useState, useEffect, useCallback } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { X, ChevronRight, ChevronLeft, HelpCircle } from "lucide-react";

const WALKTHROUGH_STEPS = {
  admin: [
    { title: "Welcome to VerifyPro", description: "This walkthrough will guide you through the main features. You can enable/disable it anytime from Help Center.", target: null },
    { title: "Dashboard", description: "See total tests, pass rates, recent tests, and common failure points at a glance.", target: '[data-testid="dashboard-link"], [href="/admin"]' },
    { title: "Product Types & Products", description: "Create product categories and add products. Each product has its own AI tolerance configuration (defect sensitivity, OCR mode, flow thresholds).", target: '[href*="product"]' },
    { title: "Verification Steps", description: "Define the 15 verification steps for each product. Each step has a CV task type, pass criteria, and expected values.", target: '[href*="steps"]' },
    { title: "AI Finetuning", description: "Upload reference images (positive = correct, negative = defect) to train the AI. More images = better accuracy. Check the ref count per step.", target: '[href*="finetuning"]' },
    { title: "Test Reports", description: "View all tests, filter by date/status, download PDF reports (with or without images), and track progress.", target: '[href*="reports"]' },
    { title: "Analytics", description: "Failure trends, AI confidence distribution, retry rates, and Excel export. Identifies which steps need more reference images.", target: '[href*="analytics"]' },
    { title: "Compare Tests", description: "Select 2+ tests to compare step-by-step results side by side.", target: '[href*="compare"]' },
    { title: "User Management", description: "Create QC Engineer accounts. Three roles: Super Admin > Admin > QC Engineer.", target: '[href*="users"]' },
    { title: "Help Center", description: "FAQs, workflow guides, suggested questions, and this walkthrough. Disable walkthrough from here.", target: '[href*="help"]' },
    { title: "You're all set!", description: "Start by uploading reference images in AI Finetuning, then have your QC team run a test. Check Analytics after a few tests to see patterns.", target: null },
  ],
  tester: [
    { title: "Welcome, QC Engineer", description: "This walkthrough shows you how to verify products step by step.", target: null },
    { title: "Dashboard", description: "See your recent tests, start new tests, and check overall pass rates.", target: '[href*="dashboard"]' },
    { title: "Start a New Test", description: "Click 'Start New Test', select the product, enter serial number and sensor count. The system creates 15 verification steps.", target: null },
    { title: "Capture & Verify", description: "For each step: capture a photo or upload → check image quality → click 'Verify Step'. The AI analyzes in background — you can move to the next step immediately.", target: null },
    { title: "When AI is Wrong", description: "Use 'Manual Accept' to override a false failure, 'Manual Reject' to flag something AI missed, or 'Skip Step' if a step doesn't apply.", target: null },
    { title: "Re-verify", description: "If a step fails, fix the issue, take a new photo, and click 'Re-verify'. Previous attempts are saved in retry history.", target: null },
    { title: "Crop Tool", description: "After capturing, click 'Crop' to focus on the relevant area. This helps the AI analyze the right part of the image.", target: null },
    { title: "Complete & Report", description: "After all steps, click 'Complete Test'. Download PDF reports from Test Reports page.", target: '[href*="reports"]' },
    { title: "Tips for Best Results", description: "Good lighting, hold phone steady, tap to focus before capturing. The system checks image quality and warns if too dark or blurry.", target: null },
  ],
};

export function Walkthrough({ role, onClose }) {
  const [step, setStep] = useState(0);
  const steps = WALKTHROUGH_STEPS[role === "quality_engineer" || role === "tester" ? "tester" : "admin"] || WALKTHROUGH_STEPS.admin;
  const current = steps[step];

  const handleNext = () => {
    if (step < steps.length - 1) setStep(step + 1);
    else onClose();
  };

  const handlePrev = () => {
    if (step > 0) setStep(step - 1);
  };

  useEffect(() => {
    if (current.target) {
      const el = document.querySelector(current.target);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.style.outline = "3px solid #3b82f6";
        el.style.outlineOffset = "4px";
        el.style.borderRadius = "8px";
        return () => {
          el.style.outline = "";
          el.style.outlineOffset = "";
        };
      }
    }
  }, [step, current.target]);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center pb-8 pointer-events-none">
      <div className="pointer-events-auto w-full max-w-lg mx-4">
        <Card className="shadow-2xl border-blue-500/50">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-blue-500" />
                <span className="text-xs text-muted-foreground">{step + 1} of {steps.length}</span>
              </div>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onClose}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <h3 className="font-semibold text-lg mb-1">{current.title}</h3>
            <p className="text-sm text-muted-foreground mb-4">{current.description}</p>
            <div className="flex items-center justify-between">
              <div className="flex gap-1">
                {steps.map((_, i) => (
                  <div key={i} className={`w-2 h-2 rounded-full ${i === step ? "bg-blue-500" : i < step ? "bg-blue-300" : "bg-muted"}`} />
                ))}
              </div>
              <div className="flex gap-2">
                {step > 0 && (
                  <Button variant="outline" size="sm" onClick={handlePrev}>
                    <ChevronLeft className="w-4 h-4 mr-1" /> Back
                  </Button>
                )}
                <Button size="sm" onClick={handleNext}>
                  {step === steps.length - 1 ? "Done" : "Next"} <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export function useWalkthrough() {
  const [active, setActive] = useState(false);

  const startWalkthrough = useCallback(() => setActive(true), []);
  const stopWalkthrough = useCallback(() => {
    setActive(false);
    localStorage.setItem("walkthrough_seen", "true");
  }, []);

  useEffect(() => {
    const seen = localStorage.getItem("walkthrough_seen");
    if (!seen) setActive(true);
  }, []);

  return { active, startWalkthrough, stopWalkthrough };
}
